<template>
  <div v-if="invoices" class="relative mt-4 space-y-4">
    <div
      class="grid w-full grid-cols-1 gap-2"
      :class="{
        'md:grid-cols-[max-content_1fr_repeat(4,max-content)]': showAllocatedAmounts,
        'md:grid-cols-[max-content_1fr_repeat(3,max-content)]': !showAllocatedAmounts,
      }"
    >
      <InvoiceListItem
        v-for="invoice in invoices"
        :key="invoice.id"
        :invoice="invoice"
        :default-expanded="props.defaultExpanded"
        :booking="booking"
        :invoices="invoices"
        :payments="payments"
        :show-allocated-amounts="showAllocatedAmounts"
        :allow-customer-selection="props.allowCustomerSelection"
        @click="openInvoiceDetails(invoice)"
      />
      <div
        v-if="invoices.length === 0"
        class="col-span-full rounded-lg border border-obsidian-200 bg-white p-4"
      >
        {{ t('noInvoices') }}
      </div>
    </div>

    <InvoicesForm
      v-if="addingInvoice"
      :booking="booking"
      :invoices="invoices"
      :payments="payments"
      :hide-cancel-edit-button="props.hideCancelEditButton"
      :allow-customer-selection="props.allowCustomerSelection"
      @on-submit="handleCreateInvoice"
      @on-cancel="addingInvoice = false"
    />

    <InvoiceFromLedgerEntriesForm
      v-else-if="addingInvoiceFromLedgerEntries"
      :booking="booking"
      :payments="payments"
      :hide-cancel-edit-button="props.hideCancelEditButton"
      :is-loading="isCreatingFromLedgerEntries"
      @on-submit="handleCreateInvoiceFromLedgerEntries"
      @on-cancel="addingInvoiceFromLedgerEntries = false"
    />

    <div
      v-else-if="$can('create', 'Invoice') && !props.hideAddInvoiceButton"
      class="flex flex-wrap justify-end gap-2"
    >
      <CVButton
        v-if="hasOpenLedgerEntries"
        class="max-sm:w-full"
        variant="secondary"
        @click.prevent="addingInvoiceFromLedgerEntries = true"
      >
        + {{ t('addInvoiceFromLedgerEntries') }}
      </CVButton>
      <CVButton class="max-sm:w-full" @click.prevent="addingInvoice = true"
        >+ {{ t('addInvoice') }}</CVButton
      >
    </div>

    <div
      v-if="isCreating || isSending"
      class="absolute inset-[-10px] flex items-center justify-center bg-black bg-opacity-30"
    >
      <Spinner />
    </div>

    <Modal
      v-if="selectedInvoice"
      :model-value="showInvoiceDetailsModal"
      :show-close="true"
      @update:model-value="showInvoiceDetailsModal = $event"
    >
      <div class="space-y-6">
        <h2>
          <span>
            {{ t('invoiceDetails') }} –
            {{ selectedInvoice.completeInvoiceNumber ?? t('invoiceNrNotAvailable') }}
          </span>
          –
          <span v-if="selectedInvoice.title" class="text-obsidian-500">
            {{ selectedInvoice.title }}
          </span>
        </h2>
        <InvoiceDetails :key="selectedInvoice.id" :invoice="selectedInvoice" />
      </div>
    </Modal>
  </div>
  <div v-else class="flex h-20 items-center justify-center">
    <Spinner />
  </div>
</template>

<script lang="ts" setup>
import { FindAllPaymentsSort } from '@/entities/find-all-payments-sort.enum';
import { FindAllInvoicesSort } from '@/entities/invoices/find-all-invoices-sort.enum';
import type { CreateInvoiceDto, Invoice } from '@/entities/invoices/invoice.entity';
import { Order } from '@/entities/pagination/order.enum';
import { useFlattenPaginatedData } from '@/hooks/use-flatten-paginated-data';
import { useInvoicePdfs } from '@/hooks/use-invoice-pdf';
import { useI18n } from 'vue-i18n';
import { createToast } from 'mosha-vue-toastify';
import {
  useCreateInvoice,
  useCreateInvoiceFromLedgerEntries,
  useInvoicesWithQuery,
} from '@/queries/use-invoices';
import { useLedgerEntries } from '@/queries/use-ledger';
import { usePaymentsWithQuery } from '@/queries/use-payments';
import { computed, ref } from 'vue';
import InvoicesForm from './InvoicesForm.vue';
import InvoiceListItem from './InvoiceListItem.vue';
import InvoiceFromLedgerEntriesForm from './InvoiceFromLedgerEntriesForm.vue';
import type { Booking } from '@/entities/bookings/booking.entity';
import CVButton from '@/components/buttons/CVButton.vue';
import { InvoiceType, type CreateInvoiceFromLedgerEntriesDto } from '@carvia/ros-client-types';
import Modal from '../Modal.vue';
import InvoiceDetails from './InvoiceDetails.vue';
import { useAbility } from '@casl/vue';
import { isLedgerEntryEligibleForInvoicing } from '@/utils/is-ledger-entry-eligible-for-invoicing';

const props = defineProps<{
  booking: Booking;
  defaultExpanded?: boolean;
  filterInvoiceTypes?: InvoiceType[];
  hideAddInvoiceButton?: boolean;
  hideCancelEditButton?: boolean;
  allowCustomerSelection?: boolean;
}>();

const { t } = useI18n();
const ability = useAbility();

const showAllocatedAmounts = computed(() => ability.can('list', 'InvoiceAllocation'));

const addingInvoice = ref(false);
const addingInvoiceFromLedgerEntries = ref(false);
const selectedInvoice = ref<Invoice | null>(null);
const showInvoiceDetailsModal = ref(false);

const openInvoiceDetails = (invoice: Invoice) => {
  selectedInvoice.value = invoice;
  showInvoiceDetailsModal.value = true;
};

const queryParams = {
  filter: [
    {
      bookingIds: [props.booking.id],
      types: props.filterInvoiceTypes,
    },
  ],
  sort: FindAllInvoicesSort.INVOICE_DATE,
  order: Order.ASC,
  limit: 100000,
};

const { data: invoicesData } = useInvoicesWithQuery(queryParams);
const { mutateAsync: createInvoice, isPending: isCreating } = useCreateInvoice();
const { mutateAsync: createFromLedgerEntries, isPending: isCreatingFromLedgerEntries } =
  useCreateInvoiceFromLedgerEntries();
const { data: paymentsData } = usePaymentsWithQuery({
  bookingIds: [props.booking.id],
  limit: 100000,
  sort: FindAllPaymentsSort.CREATED_DATE,
  order: Order.ASC,
});

const { data: ledgerEntriesData } = useLedgerEntries(
  computed(() => ({
    bookingIds: [props.booking.id],
    limit: 200,
  })),
  ability.can('list', 'LedgerEntry'),
);

const { sendInvoiceToCustomer, isSending } = useInvoicePdfs();

const invoices = useFlattenPaginatedData(invoicesData);
const payments = useFlattenPaginatedData(paymentsData);
const ledgerEntriesFlat = useFlattenPaginatedData(ledgerEntriesData);
const hasOpenLedgerEntries = computed(() =>
  (ledgerEntriesFlat.value ?? []).some(isLedgerEntryEligibleForInvoicing),
);

const handleCreateInvoice = async (newInvoice: CreateInvoiceDto, sendInvoiceOnSave: boolean) => {
  const { id } = await createInvoice(newInvoice);
  if (sendInvoiceOnSave) {
    await sendInvoiceToCustomer(id);
  }
  addingInvoice.value = false;
};

const handleCreateInvoiceFromLedgerEntries = async (
  values: CreateInvoiceFromLedgerEntriesDto,
  sendInvoiceOnSave: boolean,
) => {
  const result = await createFromLedgerEntries(values);
  const createdDocuments = [...result.regularInvoices, ...result.reversalInvoices];
  const unabsorbedCount = result.unabsorbedCredits?.length ?? 0;

  if (createdDocuments.length === 0) {
    createToast(t('noInvoiceCreatedFromSelection'), {
      position: 'bottom-right',
      type: 'info',
    });
  } else {
    createToast(t('invoicesCreated', { count: createdDocuments.length }), {
      position: 'bottom-right',
      type: 'success',
    });
    if (sendInvoiceOnSave) {
      for (const invoice of createdDocuments) {
        await sendInvoiceToCustomer(invoice.id);
      }
    }
  }

  if (unabsorbedCount > 0) {
    createToast(t('unabsorbedCreditsRemain', { count: unabsorbedCount }), {
      position: 'bottom-right',
      type: 'warning',
    });
  }

  addingInvoiceFromLedgerEntries.value = false;
};
</script>

<i18n lang="json">
{
  "en": {
    "invoiceNrNotAvailable": "INr. n/a",
    "addInvoice": "Add Invoice",
    "addInvoiceFromLedgerEntries": "Create invoice from open receivable entries",
    "noInvoices": "This booking has no invoices",
    "invoiceDetails": "Invoice Details",
    "noInvoiceCreatedFromSelection": "No invoice could be created from the selected receivable entries. Remaining credits stay open as receivable entries.",
    "invoicesCreated": "Created {count} invoice(s)",
    "unabsorbedCreditsRemain": "{count} credit(s) could not be absorbed and remain open as receivable entries"
  },
  "de": {
    "invoiceNrNotAvailable": "RNr. n/a",
    "addInvoice": "Rechnung hinzufügen",
    "addInvoiceFromLedgerEntries": "Rechnung aus offenen Forderungseinträgen erstellen",
    "noInvoices": "Diese Buchung hat noch keine Rechnungen",
    "invoiceDetails": "Rechnungsdetails",
    "noInvoiceCreatedFromSelection": "Aus der Auswahl konnte keine Rechnung erstellt werden. Verbleibende Gutschriften bleiben als offene Forderungseinträge sichtbar.",
    "invoicesCreated": "{count} Rechnung(en) erstellt",
    "unabsorbedCreditsRemain": "{count} Gutschrift(en) konnten nicht verrechnet werden und bleiben als offene Forderungseinträge sichtbar"
  },
  "es": {
    "invoiceNrNotAvailable": "N.º fact. s/d",
    "addInvoice": "Añadir factura",
    "addInvoiceFromLedgerEntries": "Crear factura a partir de partidas pendientes de cobro",
    "noInvoices": "Esta reserva no tiene facturas",
    "invoiceDetails": "Detalle de la factura",
    "noInvoiceCreatedFromSelection": "No se pudo crear ninguna factura con la selección. Los créditos restantes permanecen abiertos en el libro mayor.",
    "invoicesCreated": "Se crearon {count} factura(s)",
    "unabsorbedCreditsRemain": "{count} crédito(s) no pudieron absorberse y permanecen abiertos en el libro mayor"
  }
}
</i18n>
